/**
 * ConversationSearch
 * Inline search input + results shown below the tabs in the sidebar.
 * Results use the same card layout as conversation list items.
 */

import { Search, X } from "lucide-react"
import { useState } from "react"
import { Link, useLocation } from "react-router-dom"
import aiAvatarImg from "@/assets/icons/ai-conversation-avatar.png"
import ritualityAiIcon from "@/assets/icons/rituality-ai.svg"
import { Avatar } from "@/components/ui/avatar"
import { useTranslation } from "@/language"
import { cn } from "@/lib/utils"
import { useConversationSearch } from "../../hooks/useConversationSearch"
import type { ChatSearchResult } from "../../types/unified-chat.types"

function getResultRoute(result: ChatSearchResult): string {
  switch (result.type) {
    case "ai":
      return `/chats/ai/${result.id}`
    case "dm":
      return `/chats/dm/${result.id}`
    case "ritual-group":
      return `/chats/ritual/${result.id}`
    default:
      return "/chats"
  }
}

function ResultIcon({ result }: { result: ChatSearchResult }) {
  if (result.type === "ai") {
    return (
      <img src={aiAvatarImg} alt="Rituality AI" className="size-[30px] rounded-full shrink-0" />
    )
  }
  return (
    <Avatar
      src={result.profilePhoto || undefined}
      alt={result.name}
      fallback={result.name || "?"}
      size="sm"
    />
  )
}

function getTypeBadgeLabel(type: ChatSearchResult["type"]): string | null {
  switch (type) {
    case "ai":
      return "Rituality AI"
    default:
      return null
  }
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

function highlightMatch(text: string, query: string): React.ReactNode {
  const trimmed = query.trim()
  if (!trimmed) return text
  const parts = text.split(new RegExp(`(${escapeRegex(trimmed)})`, "gi"))
  return parts.map((part, index) =>
    part.toLowerCase() === trimmed.toLowerCase() ? (
      <mark key={`${index}-${part}`} className="bg-[var(--color-search-highlight)] text-inherit">
        {part}
      </mark>
    ) : (
      part
    ),
  )
}

function SearchResultItem({ result, query }: { result: ChatSearchResult; query: string }) {
  const location = useLocation()
  const route = getResultRoute(result)
  const isActive = location.pathname === route
  const badgeLabel = getTypeBadgeLabel(result.type)

  return (
    <Link
      to={route}
      className={cn(
        "group relative w-full flex flex-col gap-[10px] px-[20px] py-[15px] text-left transition-colors no-underline border-b border-border",
        isActive ? "bg-[var(--color-chat-active)]" : "hover:bg-accent",
      )}
    >
      {/* Top row: avatar + name */}
      <div className="flex items-center h-[30px]">
        <div className="flex items-center gap-[10px] flex-1 min-w-0">
          <ResultIcon result={result} />
          <p className="text-[13px] font-semibold text-foreground truncate">
            {highlightMatch(result.name, query)}
          </p>
        </div>
      </div>

      {/* Match context */}
      <p className="text-[13px] font-normal text-foreground leading-[25px] line-clamp-2">
        {highlightMatch(result.matchContext, query)}
      </p>

      {/* Type badge */}
      {badgeLabel && (
        <div className="flex items-center gap-[5px]">
          <img src={ritualityAiIcon} alt="" className="size-[14px]" />
          <span className="text-[13px] font-semibold text-[var(--color-chat-badge)]">
            {badgeLabel}
          </span>
        </div>
      )}
    </Link>
  )
}

interface ConversationSearchProps {
  onClose: () => void
}

export function ConversationSearch({ onClose }: ConversationSearchProps) {
  const { t } = useTranslation("chats")
  const [query, setQuery] = useState("")
  const { data, isLoading } = useConversationSearch(query)

  return (
    <>
      {/* Search input bar */}
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-[18px] text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={t("common.searchPlaceholder")}
              className="w-full h-10 pl-10 pr-9 rounded-[10px] bg-[var(--color-chat-input-bg)] border border-[var(--color-chat-input-border)] text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[rgba(242,240,235,0.3)]"
              autoFocus
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={t("common.clearSearch")}
              >
                <X className="size-4" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[13px] font-semibold text-foreground hover:opacity-70 transition-opacity shrink-0"
          >
            {t("common.cancel")}
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto">
        {query.length < 2 ? null : isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="size-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !data?.results.length ? (
          <div className="text-center py-8">
            <p className="text-[13px] text-muted-foreground">No results found</p>
          </div>
        ) : (
          data.results.map(result => (
            <SearchResultItem key={`${result.type}-${result.id}`} result={result} query={query} />
          ))
        )}
      </div>
    </>
  )
}
